/**
 * /people/:id — a person's whole schedule in one place (task 23 §5.3).
 *
 * The profile (derived line manager §2.2, unit, base calendar and work types)
 * comes first. The shared daily availability grid follows inside the schedule
 * section (one person row, no capacity chart), with records below. Vacation,
 * sick leave and part-time events are entered from «Добавить отсутствие»; overtime,
 * which always carries a project, comes from «Добавить переработку».
 *
 * Work types live in the profile as two subsections (task 130 §5.2): the
 * position's set (locked) and the ad-hoc additions, editable in place by whoever
 * leads the person's unit — gated by `profile.can_edit_work_types`, a separate flag.
 *
 * The input buttons are gated by `profile.can_edit_schedule` (the backend's
 * role-matrix verdict): PMO/admin for anyone, a lead for their subtree,
 * worker/viewer read-only. The gate is UX only — the backend re-checks scope on
 * every write, so hiding the button is not the security boundary.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/layout/AppShell";
import { AvailabilityCapacityGrid } from "@/components/availability/AvailabilityCapacityGrid";
import {
  adaptPersonScheduleEvents,
  buildCalendarRange,
  resetStartMonth,
  shiftStartMonth,
  type HorizonMonths,
} from "@/components/availability/model";
import { Button } from "@/components/ui/button";
import { Surface } from "@/components/ui/surface";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AbsenceFormDialog } from "@/components/people/AbsenceFormDialog";
import { OvertimeFormDialog } from "@/components/people/OvertimeFormDialog";
import { PersonProfileSection } from "@/components/people/PersonProfileSection";
import { PersonWorkTypesSection } from "@/components/people/PersonWorkTypesSection";
import { ConfirmDialog } from "@/components/templates/ConfirmDialog";
import { useDeleteScheduleEvent, usePerson } from "@/hooks/usePeople";
import { useIsNonWorkingDay } from "@/hooks/useNonWorkingDays";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { todayKey } from "@/lib/dateKey";
import { cn } from "@/lib/utils";
import type { PersonScheduleEvent } from "@/api/people";
import { useSession } from "@/store/session";

interface Props {
  userId: number;
  edit?: boolean;
  onEditChange?: (editing: boolean) => void;
}

const HORIZONS: readonly HorizonMonths[] = [1, 3, 6, 12];

function shortDate(iso: string, locale: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(locale === "en" ? "en-GB" : "ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

export function PeopleDetailPage({
  userId,
  edit = false,
  onEditChange,
}: Props): React.ReactElement {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "ru";
  const personQuery = usePerson(userId);
  const actor = useSession((state) => state.user);
  const deleteMut = useDeleteScheduleEvent();
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [overtimeOpen, setOvertimeOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PersonScheduleEvent | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [horizon, setHorizon] = useState<HorizonMonths>(3);
  const [startMonth, setStartMonth] = useState(() => resetStartMonth());
  const profile = personQuery.data?.profile;
  const person = personQuery.data;
  const isNonWorkingDay = useIsNonWorkingDay(profile?.calendar_id ?? null);
  const baseCalendarRange = useMemo(
    () => buildCalendarRange(startMonth, horizon),
    [startMonth, horizon],
  );
  const calendarRange = useMemo(
    () => ({
      ...baseCalendarRange,
      days: baseCalendarRange.days.map((day) => ({
        ...day,
        isWeekend: isNonWorkingDay(day.date),
      })),
    }),
    [baseCalendarRange, isNonWorkingDay],
  );

  const events = useMemo(() => personQuery.data?.events ?? [], [personQuery.data?.events]);
  const recordEvents = useMemo(() => {
    const today = todayKey();
    const active = events
      .filter((event) => event.date_to >= today)
      .sort(
        (left, right) =>
          left.date_from.localeCompare(right.date_from) ||
          left.date_to.localeCompare(right.date_to),
      )
      .map((event) => ({ event, isPast: false }));
    if (!showPast) return active;

    const past = events
      .filter((event) => event.date_to < today)
      .sort(
        (left, right) =>
          right.date_to.localeCompare(left.date_to) ||
          right.date_from.localeCompare(left.date_from),
      )
      .map((event) => ({ event, isPast: true }));
    return [...active, ...past];
  }, [events, showPast]);
  const rows = useMemo(() => {
    if (!profile) return [];
    return adaptPersonScheduleEvents(profile.id, profile.full_name, events);
  }, [profile, events]);

  async function onDelete(event: PersonScheduleEvent): Promise<void> {
    try {
      await deleteMut.mutateAsync({ userId, eventId: event.id });
      toast.success(t("people.toast.deleted"));
      setDeleteTarget(null);
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
    }
  }

  if (personQuery.isLoading) {
    return (
      <AppShell>
        <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
      </AppShell>
    );
  }
  if (personQuery.isError) {
    return (
      <AppShell>
        <ApiErrorNotice
          error={personQuery.error}
          fallbackMessage={t("common.apiError.fallback")}
          className="text-destructive text-sm"
          data-testid="person-not-found"
        />
      </AppShell>
    );
  }
  if (!profile || !person) {
    return (
      <AppShell>
        <div className="text-destructive text-sm" data-testid="person-not-found">
          {t("people.notFound")}
        </div>
      </AppShell>
    );
  }

  const canEdit = profile.can_edit_schedule;
  // Отдельный флаг, не `can_edit_schedule` (задача 123 §2.2): работы правит
  // admin ∪ тот, кто ведёт/замещает подразделение человека.
  const canEditWorkTypes = profile.can_edit_work_types;
  const workTypes = profile.work_types ?? [];

  const leadershipPositions = person.leadership_positions ?? [];

  const setProfileEditing = (editing: boolean): void => onEditChange?.(editing);

  return (
    <AppShell>
      <div className="flex items-center gap-4">
        <div className="flex flex-col gap-1">
          {profile.unit_id != null ? (
            <Link
              to="/units/$unitId"
              params={{ unitId: String(profile.unit_id) }}
              className="text-muted-foreground text-xs hover:underline"
            >
              {t("people.back")}
            </Link>
          ) : (
            <Link
              to="/people"
              search={{ tab: "structure" }}
              className="text-muted-foreground text-xs hover:underline"
            >
              {t("people.backUnits")}
            </Link>
          )}
          <h1 className="text-2xl font-semibold" data-testid="person-name">
            {profile.full_name}
          </h1>
        </div>
      </div>

      <section>
        <PersonProfileSection
          person={person}
          editing={edit}
          onEditChange={setProfileEditing}
          canManageProjectManager={actor?.role === "admin"}
        />

        {leadershipPositions.length > 0 && (
          <section className="mt-6" data-testid="person-leadership">
            <h3 className="text-sm font-semibold">{t("people.leadership.title")}</h3>
            <Surface className="mt-3">
              <ul className="divide-y">
                {leadershipPositions.map((position) => (
                  <li
                    key={`${position.kind}-${position.unit_id}`}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                  >
                    <Link
                      to="/units/$unitId"
                      params={{ unitId: String(position.unit_id) }}
                      className="font-medium text-[var(--accent-tech)] underline underline-offset-2"
                    >
                      {position.unit_name}
                    </Link>
                    <span className="text-muted-foreground">
                      {t(`people.leadership.${position.kind}`)}
                      {position.until
                        ? ` · ${t("people.leadership.until", { date: shortDate(position.until, locale) })}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </Surface>
          </section>
        )}

        {person.is_active && (workTypes.length > 0 || canEditWorkTypes) && (
          <PersonWorkTypesSection
            key={userId}
            userId={userId}
            workTypes={workTypes}
            lockedIds={profile.locked_work_type_ids ?? []}
            adhocIds={profile.adhoc_work_type_ids ?? []}
            canEdit={canEditWorkTypes}
          />
        )}
      </section>

      <section className="mt-8">
        <h2 className="text-base font-semibold">{t("people.scheduleTitle")}</h2>
        <section className="mt-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">{t("people.records")}</h3>
            <div className="flex flex-wrap items-center gap-3">
              <label
                htmlFor="people-show-past"
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <Switch id="people-show-past" checked={showPast} onCheckedChange={setShowPast} />
                {t("people.showPast")}
              </label>
              {canEdit && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAbsenceOpen(true)}
                    data-testid="person-add-absence"
                  >
                    {t("people.addAbsence")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOvertimeOpen(true)}
                    data-testid="person-add-overtime"
                  >
                    {t("people.addOvertime")}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {recordEvents.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="person-records-empty">
              {t("people.recordsEmpty")}
            </p>
          ) : (
            <Surface data-testid="person-records">
              <ul className="divide-y">
                {recordEvents.map(({ event, isPast }) => (
                  <li
                    key={event.id}
                    className={cn(
                      "flex items-center justify-between gap-3 px-3 py-2 text-sm",
                      isPast && "text-muted-foreground",
                    )}
                    data-testid="person-record-row"
                    data-past={isPast ? "true" : undefined}
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="font-medium">{event.event_type.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {shortDate(event.date_from, locale)} — {shortDate(event.date_to, locale)}
                        {event.duration_half_hours != null &&
                          ` · ${event.event_type.event_group === "overtime" ? "+" : "−"}${t(
                            "people.hours",
                            {
                              value: event.duration_half_hours / 2,
                            },
                          )}`}
                        {event.event_type.event_group === "overtime" &&
                          event.payable &&
                          ` · ${t("people.payableShort")}`}
                      </span>
                    </div>
                    {canEdit && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t("people.delete")}
                            data-testid={`person-record-delete-${event.id}`}
                            onClick={() => setDeleteTarget(event)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("people.delete")}</TooltipContent>
                      </Tooltip>
                    )}
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </section>

        <div className="mt-6 mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">{t("people.calendarTitle")}</h3>
          <div className="availability-capacity-controls">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("availability.previousMonth")}
              data-testid="availability-capacity-previous"
              onClick={() => setStartMonth((current) => shiftStartMonth(current, -1))}
            >
              <ChevronLeft aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="availability-capacity-reset"
              onClick={() => setStartMonth(resetStartMonth())}
            >
              <RotateCcw aria-hidden="true" />
              {t("availability.currentMonth")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("availability.nextMonth")}
              data-testid="availability-capacity-next"
              onClick={() => setStartMonth((current) => shiftStartMonth(current, 1))}
            >
              <ChevronRight aria-hidden="true" />
            </Button>
            <div
              className="availability-capacity-horizons"
              role="group"
              aria-label={t("availability.horizonLabel")}
            >
              {HORIZONS.map((months) => (
                <Button
                  key={months}
                  type="button"
                  variant={horizon === months ? "default" : "outline"}
                  aria-pressed={horizon === months}
                  data-testid={`availability-capacity-horizon-${months}`}
                  onClick={() => setHorizon(months)}
                >
                  {t("availability.horizon", { count: months })}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <Surface className="availability-capacity-surface">
          <AvailabilityCapacityGrid
            range={calendarRange}
            rows={rows}
            capacity={[]}
            showCapacity={false}
          />
        </Surface>
      </section>

      {canEdit && (
        <>
          <AbsenceFormDialog open={absenceOpen} onOpenChange={setAbsenceOpen} userId={userId} />
          <OvertimeFormDialog open={overtimeOpen} onOpenChange={setOvertimeOpen} userId={userId} />
        </>
      )}
      <ConfirmDialog
        open={deleteTarget != null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("people.deleteConfirm")}
        body={t("people.deleteConfirmBody", {
          person: profile.full_name,
          event: deleteTarget?.event_type.name ?? "",
          from: deleteTarget ? shortDate(deleteTarget.date_from, locale) : "",
          to: deleteTarget ? shortDate(deleteTarget.date_to, locale) : "",
        })}
        submitLabel={t("common.delete")}
        onConfirm={async () => {
          if (deleteTarget) await onDelete(deleteTarget);
        }}
        destructive
        pending={deleteMut.isPending}
        testId="person-record-delete-confirm"
      />
    </AppShell>
  );
}
